import { useEffect, useRef, useState } from "react";
import { AuthAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/AuthAPIHandler";
import { OTP_LENGTH } from "../../../../enums/AuthFlowEnums";
import { useOtpResendCountdown } from "./useOtpResendCountdown";

const SUCCESS_REDIRECT_DELAY_MS = 2000;

type RegistrationOtpParams = {
    email: string;
    name: string;
    password: string;
    /** Bot-trap value captured on the registration form. */
    honeypot?: string;
    onRegistered: () => void;
};

/** Single source of truth for email-verification + registration, shared by web and mobile. */
export const useRegistrationOtpVerification = ({ email, name, password, honeypot = "", onRegistered }: RegistrationOtpParams) => {
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [hasRegistered, setHasRegistered] = useState(false);
    const { canResend, resendButtonText, restartCountdown } = useOtpResendCountdown();

    const onRegisteredRef = useRef(onRegistered);
    onRegisteredRef.current = onRegistered;

    useEffect(() => {
        if (!hasRegistered) return;

        const timerId = window.setTimeout(() => onRegisteredRef.current(), SUCCESS_REDIRECT_DELAY_MS);
        return () => window.clearTimeout(timerId);
    }, [hasRegistered]);

    const verifyOtpAndRegister = async () => {
        if (otp.length !== OTP_LENGTH) return;

        setError("");
        setIsLoading(true);
        try {
            await AuthAPIHandler.shared.register(name, email, password, password, otp, honeypot);
            setHasRegistered(true);
        } catch (requestError) {
            setError(requestError as string);
        } finally {
            setIsLoading(false);
        }
    };

    const resendOtp = async () => {
        setError("");
        setIsResending(true);
        try {
            const success = await AuthAPIHandler.shared.requestOtpForEmailVerification(email, name, honeypot);
            if (success) restartCountdown();
        } catch (requestError) {
            setError(requestError as string);
        } finally {
            setIsResending(false);
        }
    };

    return {
        otp,
        setOtp,
        error,
        isLoading,
        isResending,
        hasRegistered,
        canResend,
        resendButtonText,
        verifyOtpAndRegister,
        resendOtp,
    };
};

export default useRegistrationOtpVerification;
